import { getUser } from "../utils/authService";
/**
 * Archive Management Page
 * Allows Master Admin and Project Managers to manage archived equipment and staff
 * Features: View archived items, restore items, archive new items with reason
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Archive, Truck, Users, RotateCcw, AlertTriangle, Search,
  Calendar, User, FileText, ChevronDown, ChevronUp, Filter
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import BackNavigation from "../components/BackNavigation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ArchiveManagement = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [archivedEquipment, setArchivedEquipment] = useState([]);
  const [archivedEmployees, setArchivedEmployees] = useState([]);
  const [archiveLogs, setArchiveLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("equipment");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedLogs, setExpandedLogs] = useState(false);
  
  // Restore dialog
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [restoreType, setRestoreType] = useState("");

  const user = getUser() || {};
  const canManageArchives = ["Master Admin", "Admin", "Project Manager", "HR Manager"].includes(user.role);

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchArchivedItems();
    } else {
      fetchArchivedItems();
    }
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Failed to fetch project:", error);
    }
  };

  const fetchArchivedItems = async () => {
    setLoading(true);
    try {
      const params = projectId ? `?project_id=${projectId}` : "";
      const response = await apiClient.get(`/archives${params}`);
      setArchivedEquipment(response.data.archived_equipment || []);
      setArchivedEmployees(response.data.archived_employees || []);
      setArchiveLogs(response.data.archive_logs || []);
    } catch (error) {
      console.error("Failed to fetch archived items:", error);
      toast.error("Failed to load archived items");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!itemToRestore) return;
    
    try {
      const endpoint = restoreType === "equipment" 
        ? `/equipment/${itemToRestore.id}/restore`
        : `/employees/${itemToRestore.id}/restore`;
      
      await apiClient.post(endpoint);
      toast.success(`${restoreType === "equipment" ? "Equipment" : "Employee"} restored successfully`);
      setRestoreDialogOpen(false);
      setItemToRestore(null);
      fetchArchivedItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore item");
    }
  };

  const openRestoreDialog = (item, type) => {
    setItemToRestore(item);
    setRestoreType(type);
    setRestoreDialogOpen(true);
  };

  const filteredEquipment = archivedEquipment.filter(eq => 
    eq.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    eq.equipment_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredEmployees = archivedEmployees.filter(emp =>
    emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.employee_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!canManageArchives) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-2xl mx-auto text-center py-20">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-error" />
          <h2 className="font-heading font-bold text-2xl text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">Only Master Admin and Project Managers can access archive management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          {projectId && <BackNavigation />}
          <div className="flex items-center justify-between mt-4">
            <div>
              <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
                ARCHIVE MANAGEMENT
              </h1>
              <p className="text-slate-600 mt-1">
                {projectId && project ? `${project.name} - ` : ""}
                Manage archived equipment and staff records
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search archives..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary w-64"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="stat-card bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-l-amber-500">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-200 rounded-sm">
                <Archive className="w-6 h-6 text-amber-700" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Total Archived</p>
                <p className="font-heading font-bold text-2xl text-amber-700">
                  {archivedEquipment.length + archivedEmployees.length}
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-l-blue-500">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-200 rounded-sm">
                <Truck className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Archived Equipment</p>
                <p className="font-heading font-bold text-2xl text-blue-700">{archivedEquipment.length}</p>
              </div>
            </div>
          </div>
          <div className="stat-card bg-gradient-to-br from-purple-50 to-purple-100 border-l-4 border-l-purple-500">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-200 rounded-sm">
                <Users className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-600 font-medium">Archived Staff</p>
                <p className="font-heading font-bold text-2xl text-purple-700">{archivedEmployees.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-sm w-fit">
          {[
            { key: "equipment", label: "Equipment", icon: Truck, count: filteredEquipment.length },
            { key: "staff", label: "Staff", icon: Users, count: filteredEmployees.length }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                activeTab === tab.key ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-primary'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.key ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Equipment Tab */}
            {activeTab === "equipment" && (
              <div className="bg-white rounded-sm border shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-heading font-semibold text-slate-800">Archived Equipment (De-hired)</h3>
                </div>
                {filteredEquipment.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Equipment</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Type</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Owner</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Archived Date</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Reason</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Archived By</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredEquipment.map((eq) => (
                          <tr key={eq.id} className="hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-800">{eq.name}</p>
                                <p className="text-xs text-slate-500">{eq.equipment_id}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                                {eq.equipment_type}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">{eq.owner_type}</td>
                            <td className="py-3 px-4 text-sm font-mono">
                              {formatDate(eq.archive_info?.archived_at)}
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm text-slate-700 max-w-xs truncate" title={eq.archive_info?.reason}>
                                {eq.archive_info?.reason || "-"}
                              </p>
                              {eq.archive_info?.remarks && (
                                <p className="text-xs text-slate-500 italic truncate" title={eq.archive_info?.remarks}>
                                  {eq.archive_info.remarks}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm">{eq.archive_info?.archived_by_name || "-"}</td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => openRestoreDialog(eq, "equipment")}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-sm text-sm font-medium hover:bg-green-200 transition-colors"
                              >
                                <RotateCcw className="w-4 h-4" />
                                Restore
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No archived equipment found</p>
                  </div>
                )}
              </div>
            )}

            {/* Staff Tab */}
            {activeTab === "staff" && (
              <div className="bg-white rounded-sm border shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-heading font-semibold text-slate-800">Archived Staff (Removed)</h3>
                </div>
                {filteredEmployees.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Employee</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Designation</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Department</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Archived Date</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Reason</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Archived By</th>
                          <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredEmployees.map((emp) => (
                          <tr key={emp.id} className="hover:bg-slate-50">
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium text-slate-800">{emp.name}</p>
                                <p className="text-xs text-slate-500">{emp.employee_code}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm">{emp.designation}</td>
                            <td className="py-3 px-4">
                              <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                                {emp.department}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm font-mono">
                              {formatDate(emp.archive_info?.archived_at)}
                            </td>
                            <td className="py-3 px-4">
                              <p className="text-sm text-slate-700 max-w-xs truncate" title={emp.archive_info?.reason}>
                                {emp.archive_info?.reason || "-"}
                              </p>
                              {emp.archive_info?.remarks && (
                                <p className="text-xs text-slate-500 italic truncate" title={emp.archive_info?.remarks}>
                                  {emp.archive_info.remarks}
                                </p>
                              )}
                            </td>
                            <td className="py-3 px-4 text-sm">{emp.archive_info?.archived_by_name || "-"}</td>
                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => openRestoreDialog(emp, "employee")}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-sm text-sm font-medium hover:bg-green-200 transition-colors"
                              >
                                <RotateCcw className="w-4 h-4" />
                                Restore
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>No archived staff found</p>
                  </div>
                )}
              </div>
            )}

            {/* Archive Activity Log */}
            <div className="mt-6 bg-white rounded-sm border shadow-sm overflow-hidden">
              <div 
                className="p-4 border-b bg-slate-50 flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedLogs(!expandedLogs)}
              >
                <h3 className="font-heading font-semibold text-slate-800">Archive Activity Log</h3>
                <button className="p-1 hover:bg-slate-200 rounded">
                  {expandedLogs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>
              {expandedLogs && (
                <div className="p-4 max-h-80 overflow-y-auto">
                  {archiveLogs.length > 0 ? (
                    <div className="space-y-3">
                      {archiveLogs.map((log, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm">
                          <div className={`p-2 rounded-full ${
                            log.action.includes("RESTORED") ? "bg-green-100" : "bg-amber-100"
                          }`}>
                            {log.action.includes("RESTORED") ? (
                              <RotateCcw className={`w-4 h-4 ${log.action.includes("RESTORED") ? "text-green-600" : "text-amber-600"}`} />
                            ) : (
                              <Archive className="w-4 h-4 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm">
                              <span className="font-medium">{log.performed_by_name}</span>
                              {" "}
                              <span className={log.action.includes("RESTORED") ? "text-green-600" : "text-amber-600"}>
                                {log.action.includes("RESTORED") ? "restored" : "archived"}
                              </span>
                              {" "}
                              <span className="font-medium">{log.entity_name}</span>
                              {log.employee_code && <span className="text-slate-500"> ({log.employee_code})</span>}
                            </p>
                            {log.reason && (
                              <p className="text-xs text-slate-500 mt-1">Reason: {log.reason}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">{formatDate(log.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No archive activity yet</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Restore Confirmation Dialog */}
        <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Confirm Restore</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-slate-600">
                Are you sure you want to restore{" "}
                <span className="font-semibold text-slate-800">
                  {itemToRestore?.name || itemToRestore?.employee_code}
                </span>
                ?
              </p>
              <p className="text-sm text-slate-500 mt-2">
                This will move the {restoreType} back to active status.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRestoreDialogOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                className="px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700"
              >
                Restore
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ArchiveManagement;
